import { makeStyles, mergeClasses } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: 'var(--space-m) 0',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 'var(--space-m)',
  },
  label: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  value: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 'var(--fs-400)',
  },
  unlimited: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-300)',
    fontWeight: 400,
  },
  track: {
    position: 'relative',
    width: '100%',
    height: '8px',
    background: 'var(--bg-raised)',
    borderRadius: 'var(--radius-circular)',
    overflow: 'hidden',
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    transition: 'width 0.4s ease, background-color 0.2s',
    borderRadius: 'var(--radius-circular)',
  },
  fillGreen:  { background: 'var(--ok)' },
  fillYellow: { background: 'var(--warn)' },
  fillRed:    { background: 'var(--bad)' },
  fillOrange: { background: 'var(--bad)' },
  fillGray:   { background: 'var(--border-default)', opacity: 0.5 },
  hint: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    fontFamily: 'var(--font-mono)',
  },
});

export type UsageState = 'ok' | 'warn' | 'danger' | 'overage' | 'unlimited';

export interface UsageMeterProps {
  label: string;
  current: number;
  /** `null` = sin tope de plan (no muestra "/límite" ni barra; el gate real es la bolsa de tokens). */
  limit: number | null;
  /** Unidad para mostrar en valor (ej "proyectos", "conceptos"). */
  unit?: string;
}

function stateFor(current: number, limit: number | null): UsageState {
  if (limit === null) return 'unlimited';
  if (limit <= 0) return 'ok';
  const pct = (current / limit) * 100;
  if (pct > 100) return 'overage';
  if (pct >= 95) return 'danger';
  if (pct >= 80) return 'warn';
  return 'ok';
}

/**
 * Medidor de consumo con 4 estados de color + estado "ilimitado".
 *
 * Estado    | Rango      | Color
 * --------- | ---------- | ----------
 * ok        | < 80 %     | verde
 * warn      | 80–94 %    | amarillo
 * danger    | 95–100 %   | rojo
 * overage   | > 100 %    | naranja
 * unlimited | limit=null | gris (decorativo, sin progreso)
 *
 * Spec: doc gerencia OPUS Total §5 (review 2026-05-15).
 */
export function UsageMeter({ label, current, limit, unit }: UsageMeterProps) {
  const styles = useStyles();
  const state = stateFor(current, limit);
  const pct = limit && limit > 0 ? Math.min((current / limit) * 100, 100) : 100;
  const fillClass =
    state === 'ok' ? styles.fillGreen :
    state === 'warn' ? styles.fillYellow :
    state === 'danger' ? styles.fillRed :
    state === 'overage' ? styles.fillOrange :
    styles.fillGray;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.label}>{label}</span>
        {limit === null ? (
          <span className={styles.value}>
            {current.toLocaleString('es-MX')}
          </span>
        ) : (
          <span className={styles.value}>
            {current.toLocaleString('es-MX')}{' '}
            <span className={styles.unlimited}>
              / {limit.toLocaleString('es-MX')}
            </span>
          </span>
        )}
      </div>
      {limit !== null && (
        <div className={styles.track}>
          <div
            className={mergeClasses(styles.fill, fillClass)}
            style={{ width: `${pct}%` }}
            aria-valuenow={current}
            aria-valuemin={0}
            aria-valuemax={limit}
            role="progressbar"
          />
        </div>
      )}
      {state === 'overage' && limit !== null && (
        <span className={styles.hint}>
          Sobre cuota: +{(current - limit).toLocaleString('es-MX')} {unit ?? ''}
        </span>
      )}
    </div>
  );
}
